import { useEffect, useState } from 'react';
import { sdk } from 'sdk';

export enum ProductCardType {
  Classic = 'Classic',
  Compact = 'Compact',
  CompactSquare = 'CompactSquare',
}

export const DEFAULT_CARD_TYPE = ProductCardType.Classic;
export const DEFAULT_SHOW_SECOND_IMAGE_ON_HOVER = false;

export interface ProductTileFeatures {
  showColorSwatches: boolean;
  showFilters: boolean;
  showAppliedFilters: boolean;
  showSort: boolean;
}

export const DEFAULT_PRODUCT_TILE_FEATURES: ProductTileFeatures = {
  showColorSwatches: false,
  showFilters: false,
  showAppliedFilters: false,
  showSort: false,
};

/** CSS values for the `--variant-*` custom properties behind the `variant-*` Tailwind tokens. */
export interface ProductVariantLook {
  border?: string;
  selectedBg?: string;
  selectedText?: string;
  selectedBorderWidth?: string;
  idleBg?: string;
  idleText?: string;
  disabledBg?: string;
  disabledText?: string;
  separator?: string;
  swatchRing?: string;
}

export interface ProductVariantConfig {
  sizeOrder?: string[];
  look?: ProductVariantLook;
}

export interface ProductConfig {
  cardType: ProductCardType;
  showSecondImageOnHover: boolean;
  features: ProductTileFeatures;
  variant: ProductVariantConfig;
}

const STORAGE_KEY = 'productConfig_cardType';
const SHOW_SECOND_IMAGE_ON_HOVER_STORAGE_KEY = 'productConfig_showSecondImageOnHover';
const FEATURES_STORAGE_KEY = 'productConfig_tileFeatures';
const VARIANT_STORAGE_KEY = 'productConfig_variant';

const VARIANT_LOOK_KEYS: (keyof ProductVariantLook)[] = [
  'border',
  'selectedBg',
  'selectedText',
  'selectedBorderWidth',
  'idleBg',
  'idleText',
  'disabledBg',
  'disabledText',
  'separator',
  'swatchRing',
];

const isValidCardType = (value: unknown): value is ProductCardType =>
  Object.values(ProductCardType).includes(value as ProductCardType);

const toBoolean = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === 'boolean') return value;

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();

    return normalized === 'true';
  }

  return fallback;
};

const toTileFeatures = (value: unknown): ProductTileFeatures => {
  const source = (value ?? {}) as Partial<Record<keyof ProductTileFeatures, unknown>>;

  return {
    showColorSwatches: toBoolean(source.showColorSwatches, DEFAULT_PRODUCT_TILE_FEATURES.showColorSwatches),
    showFilters: toBoolean(source.showFilters, DEFAULT_PRODUCT_TILE_FEATURES.showFilters),
    showAppliedFilters: toBoolean(source.showAppliedFilters, DEFAULT_PRODUCT_TILE_FEATURES.showAppliedFilters),
    showSort: toBoolean(source.showSort, DEFAULT_PRODUCT_TILE_FEATURES.showSort),
  };
};

const toVariantConfig = (sizeOrder: unknown, look: unknown): ProductVariantConfig => {
  const config: ProductVariantConfig = {};

  if (Array.isArray(sizeOrder)) {
    const sizes = sizeOrder.filter((size): size is string => typeof size === 'string');
    if (sizes.length) config.sizeOrder = sizes;
  }

  if (look && typeof look === 'object') {
    const source = look as Record<string, unknown>;
    const values = VARIANT_LOOK_KEYS.filter((key) => typeof source[key] === 'string');
    if (values.length) config.look = Object.fromEntries(values.map((key) => [key, source[key]]));
  }

  return config;
};

const readStoredCardType = (): ProductCardType | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isValidCardType(stored) ? stored : null;
  } catch {
    return null;
  }
};

const writeStoredCardType = (cardType: ProductCardType) => {
  try {
    localStorage.setItem(STORAGE_KEY, cardType);
  } catch {
    // localStorage unavailable (e.g. private browsing with storage blocked)
  }
};

const readStoredShowSecondImageOnHover = (): boolean | null => {
  try {
    const stored = localStorage.getItem(SHOW_SECOND_IMAGE_ON_HOVER_STORAGE_KEY);
    return stored === null ? null : stored === 'true';
  } catch {
    return null;
  }
};

const writeStoredShowSecondImageOnHover = (value: boolean) => {
  try {
    localStorage.setItem(SHOW_SECOND_IMAGE_ON_HOVER_STORAGE_KEY, String(value));
  } catch {
    // localStorage unavailable (e.g. private browsing with storage blocked)
  }
};

const readStoredTileFeatures = (): ProductTileFeatures | null => {
  try {
    const stored = localStorage.getItem(FEATURES_STORAGE_KEY);
    return stored ? toTileFeatures(JSON.parse(stored)) : null;
  } catch {
    return null;
  }
};

const writeStoredTileFeatures = (features: ProductTileFeatures) => {
  try {
    localStorage.setItem(FEATURES_STORAGE_KEY, JSON.stringify(features));
  } catch {
    // localStorage unavailable (e.g. private browsing with storage blocked)
  }
};

const readStoredVariantConfig = (): ProductVariantConfig | null => {
  try {
    const stored = localStorage.getItem(VARIANT_STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as ProductVariantConfig;
    return toVariantConfig(parsed.sizeOrder, parsed.look);
  } catch {
    return null;
  }
};

const writeStoredVariantConfig = (config: ProductVariantConfig) => {
  try {
    localStorage.setItem(VARIANT_STORAGE_KEY, JSON.stringify(config));
  } catch {
    // localStorage unavailable (e.g. private browsing with storage blocked)
  }
};

let cardTypeCache: ProductCardType | null = null;
let showSecondImageOnHoverCache: boolean | null = null;
let tileFeaturesCache: ProductTileFeatures | null = null;

const tileFeaturesListeners = new Set<(features: ProductTileFeatures) => void>();

const setTileFeaturesCache = (features: ProductTileFeatures) => {
  tileFeaturesCache = features;
  tileFeaturesListeners.forEach((listener) => listener(features));
};

let variantConfigCache: ProductVariantConfig | null = null;
let variantConfigFetched = false;
const variantConfigListeners = new Set<(config: ProductVariantConfig) => void>();

const setVariantConfigCache = (config: ProductVariantConfig) => {
  variantConfigCache = config;
  variantConfigFetched = true;
  variantConfigListeners.forEach((listener) => listener(config));
};

export const subscribeToProductTileFeatures = (listener: (features: ProductTileFeatures) => void): (() => void) => {
  tileFeaturesListeners.add(listener);
  return () => {
    tileFeaturesListeners.delete(listener);
  };
};

export const loadProductConfigFromStorage = (): ProductCardType | null => {
  const stored = readStoredCardType();
  if (stored) cardTypeCache = stored;
  return stored;
};

export const loadShowSecondImageOnHoverFromStorage = (): boolean | null => {
  const stored = readStoredShowSecondImageOnHover();
  if (stored !== null) showSecondImageOnHoverCache = stored;
  return stored;
};

export const loadProductTileFeaturesFromStorage = (): ProductTileFeatures | null => {
  const stored = readStoredTileFeatures();
  if (stored) setTileFeaturesCache(stored);
  return stored;
};

const fetchProductConfig = async (): Promise<ProductConfig> => {
  try {
    const response = await sdk.callAction({ actionName: 'product-config/getProductConfig' });
    if (!response.isError) {
      const data = response.data as {
        cardType: ProductCardType;
        showSecondImageOnHover?: boolean;
        variantSizeOrder?: unknown;
        variantLook?: unknown;
      } & Partial<ProductTileFeatures>;
      const cardType = isValidCardType(data.cardType) ? data.cardType : DEFAULT_CARD_TYPE;
      const showSecondImageOnHover =
        typeof data.showSecondImageOnHover === 'boolean'
          ? data.showSecondImageOnHover
          : DEFAULT_SHOW_SECOND_IMAGE_ON_HOVER;
      const features = toTileFeatures(data);
      const variant = toVariantConfig(data.variantSizeOrder, data.variantLook);

      cardTypeCache = cardType;
      showSecondImageOnHoverCache = showSecondImageOnHover;
      writeStoredCardType(cardType);
      writeStoredShowSecondImageOnHover(showSecondImageOnHover);
      setTileFeaturesCache(features);
      writeStoredTileFeatures(features);
      setVariantConfigCache(variant);
      writeStoredVariantConfig(variant);

      return { cardType, showSecondImageOnHover, features, variant };
    }
  } catch (error) {
    console.error('loadProductConfig - Failed to fetch product config:', error);
  }

  return {
    cardType: DEFAULT_CARD_TYPE,
    showSecondImageOnHover: DEFAULT_SHOW_SECOND_IMAGE_ON_HOVER,
    features: DEFAULT_PRODUCT_TILE_FEATURES,
    variant: variantConfigCache ?? {},
  };
};

// A product listing page mounts many tiles at once, all of which need the config.
// Sharing the pending request keeps that to a single call.
let pendingRequest: Promise<ProductConfig> | null = null;

export const loadProductConfig = async (): Promise<ProductConfig> => {
  if (!pendingRequest) {
    pendingRequest = fetchProductConfig().finally(() => {
      pendingRequest = null;
    });
  }

  return pendingRequest;
};

export const initializeProductConfig = () => {
  if (typeof window !== 'undefined') {
    loadProductConfig();
  }
};

export const getProductCardType = (): ProductCardType => cardTypeCache ?? DEFAULT_CARD_TYPE;

export const getProductCardTypeOrNull = (): ProductCardType | null => cardTypeCache;

export const getShowSecondImageOnHover = (): boolean => showSecondImageOnHoverCache ?? DEFAULT_SHOW_SECOND_IMAGE_ON_HOVER;

export const getProductTileFeatures = (): ProductTileFeatures => tileFeaturesCache ?? DEFAULT_PRODUCT_TILE_FEATURES;

export const getProductTileFeaturesOrNull = (): ProductTileFeatures | null => tileFeaturesCache;

/**
 * Per-brand variant selector config (size order + `--variant-*` values) from the CT `productsConfig` container.
 * Starts from the in-memory/localStorage copy and fetches at most once per page load.
 */
export const useProductVariantConfig = (): ProductVariantConfig => {
  const [config, setConfig] = useState<ProductVariantConfig>(() => variantConfigCache ?? {});

  useEffect(() => {
    variantConfigListeners.add(setConfig);

    if (!variantConfigCache) {
      const stored = readStoredVariantConfig();
      if (stored) {
        variantConfigCache = stored;
        setConfig(stored);
      }
    }

    if (!variantConfigFetched) {
      variantConfigFetched = true;
      loadProductConfig();
    }

    return () => {
      variantConfigListeners.delete(setConfig);
    };
  }, []);

  return config;
};

export const isCompactCardType = (cardType?: ProductCardType | null): boolean =>
  cardType === ProductCardType.Compact || cardType === ProductCardType.CompactSquare;
